import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import fashionMark from "../assets/nav_fashion.png";
import { pageColors } from "../mock";

/**
 * Fashion — two ambient floating brand marks (e(x)c → Excourtier,
 * kv → Kore Velum). They drift on their own; clicking one opens that
 * brand's pre-order page. No drag, no container box — they float in the
 * page itself.
 */

const ICONS = [
  { id: "exc", src: "/img/exc.png", label: "Excourtier — e(x)c", route: "/brands/excourtier", w: 198 },
  { id: "kv", src: "/img/kv.png", label: "Kore Velum", route: "/brands/kore-velum", w: 222 },
  { id: "mjb", src: "/img/mjss.webp", label: "MJ's Bodega", route: "/brands/mjs-bodega", w: 198 },
  { id: "sbs", src: "/img/swampclay.png", label: "Swampboy Soap", route: "/brands/swampboy-soap", w: 210 },
  { id: "juju", src: "/img/goodjuju.png", label: "Good Juju", href: "https://goodjuju.online", w: 184 },
  { id: "vison", src: "/img/vison.png", label: "Vison Studios", href: "https://www.visonstudios.shop", w: 172 },
  { id: "us", src: "/img/usmark.png", label: "Us!", route: "/brands/us", w: 114 },
];

const rand = (a, b) => a + Math.random() * (b - a);

const Fashion = () => {
  const navigate = useNavigate();
  const stageRef = useRef(null);
  const stateRef = useRef([]);
  const elsRef = useRef({});
  const rafRef = useRef(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    document.title = "TMM|FASHION";
  }, []);

  // Init positions relative to the viewport-sized stage.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const laneW = rect.width / ICONS.length;
    const scale = rect.width <= 760 ? 0.8 : 1;
    stateRef.current = ICONS.map((cfg, i) => ({
      ...cfg,
      w: Math.round(cfg.w * scale),
      // Start each one loosely in its own lane so they don't spawn on top
      // of each other; after that they drift independently.
      x: rand(laneW * i + 24, Math.max(laneW * i + 34, laneW * (i + 1) - cfg.w - 24)),
      // Middle lanes spawn below the hero mark so nothing covers the intro.
      y: i > 0 && i < ICONS.length - 1
        ? rand(430, Math.max(480, rect.height - 220))
        : rand(160, Math.max(200, rect.height - 220)),
      vx: rand(0.18, 0.32) * (Math.random() < 0.5 ? -1 : 1),
      vy: rand(0.14, 0.26) * (Math.random() < 0.5 ? -1 : 1),
      angle: rand(-6, 6),
      va: rand(-0.05, 0.05) || 0.02,
      // Unique snaking path per icon: heading slowly curves at its own
      // rhythm, so no two icons trace the same trajectory.
      turn: rand(0.003, 0.008) * (i % 2 ? -1 : 1),
      wf: rand(0.0003, 0.0009),
      wp: rand(0, Math.PI * 2),
    }));
    setTick((t) => t + 1);
  }, []);

  // Ambient physics — runs outside React (direct DOM transforms) so the
  // page never re-renders per frame; each icon snakes on its own curve,
  // bounces off the stage edges, and deflects only on actual contact.
  useEffect(() => {
    const step = (now) => {
      const el = stageRef.current;
      if (!el) {
        rafRef.current = requestAnimationFrame(step);
        return;
      }
      const rect = el.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      // 1. Curve heading + advance + wall-bounce each icon independently.
      stateRef.current.forEach((it) => {
        const turn = Math.sin(now * it.wf + it.wp) * it.turn;
        const cos = Math.cos(turn);
        const sin = Math.sin(turn);
        const nvx = it.vx * cos - it.vy * sin;
        it.vy = it.vx * sin + it.vy * cos;
        it.vx = nvx;
        it.x += it.vx;
        it.y += it.vy;
        it.angle += it.va;
        if (it.x < 0) { it.x = 0; it.vx = Math.abs(it.vx); }
        if (it.x + it.w > w) { it.x = w - it.w; it.vx = -Math.abs(it.vx); }
        if (it.y < 140) { it.y = 140; it.vy = Math.abs(it.vy); }
        if (it.y + 140 > h) { it.y = h - 140; it.vy = -Math.abs(it.vy); }
        it.va *= 0.996;
        // Never fully stall.
        const speed = Math.hypot(it.vx, it.vy);
        if (speed < 0.12) {
          const s = 0.22 / (speed || 0.0001);
          it.vx *= s;
          it.vy *= s;
        }
      });

      // 2. Contact-only collision — no repulsion field. Icons drift freely
      //    and only deflect at the moment their boxes actually touch.
      const items = stateRef.current;
      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          const a = items[i];
          const b = items[j];
          const touching =
            a.x < b.x + b.w && b.x < a.x + a.w &&
            a.y < b.y + 140 && b.y < a.y + 140;
          if (!touching) continue;
          const dx = (b.x + b.w / 2) - (a.x + a.w / 2);
          const dy = (b.y + 70) - (a.y + 70);
          const dist = Math.hypot(dx, dy) || 0.0001;
          const nx = dx / dist;
          const ny = dy / dist;
          // Only cancel the closing velocity so they graze apart naturally.
          const closing = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
          if (closing < 0) {
            a.vx += nx * closing * 0.5;
            a.vy += ny * closing * 0.5;
            b.vx -= nx * closing * 0.5;
            b.vy -= ny * closing * 0.5;
          }
        }
      }

      // 3. Paint via direct transforms — zero React work per frame.
      stateRef.current.forEach((it) => {
        const node = elsRef.current[it.id];
        if (node) {
          node.style.transform = `translate3d(${it.x}px, ${it.y}px, 0) rotate(${it.angle}deg)`;
        }
      });

      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <>
      <Header hoverColor={pageColors.fashion} compact={false} />
      <div className="fashion-page-open" ref={stageRef} data-testid="fashion-stage">
        <div className="page-mark-hero">
          <img src={fashionMark} alt="Fashion" className="page-mark-hero-img" />
        </div>

        <div className="fashion-intro-open">
          <p className="fashion-eyebrow">Meet our brand family, tap to learn more and shop.</p>
          <button
            type="button"
            className="btn-solid fashion-apply-brand-btn"
            onClick={() => navigate("/apply/brand")}
            data-testid="fashion-apply-brand"
          >
            Apply as a brand →
          </button>
        </div>

        {stateRef.current.map((it) => (
          <button
            key={it.id}
            type="button"
            className={`float-link${it.route || it.href ? "" : " float-static"}`}
            ref={(node) => { elsRef.current[it.id] = node; }}
            style={{
              transform: `translate3d(${it.x}px, ${it.y}px, 0) rotate(${it.angle}deg)`,
              width: it.w,
            }}
            onClick={it.route ? () => navigate(it.route) : it.href ? () => window.open(it.href, "_blank", "noopener") : undefined}
            data-testid={`float-link-${it.id}`}
            title={it.label}
            aria-label={it.label}
          >
            <img src={it.src} alt={it.label} draggable={false} />
          </button>
        ))}
      </div>
    </>
  );
};

export default Fashion;
